import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

/**
 * StatTile — the standard KPI card.
 *   <StatTile label="Occupancy" value="94%" icon={Home} trend={{value: 3.2, direction: 'up'}} />
 *
 * Deliberately compact: a row of these sits above the actual content on most
 * screens, so it must not eat the first fold. Values scale down on narrow
 * viewports so long currency strings ("KES 130,000.00") stay on one line.
 */
const StatTile = ({
    label,
    value,
    hint,
    icon: Icon,
    trend,           // { value: number, direction: 'up'|'down', suffix?: string }
    accent,          // 'primary' | 'success' | 'warning' | 'danger' | undefined
    loading = false,
}) => {
    const accentBg = {
        primary: 'bg-primary-50 text-primary-700',
        success: 'bg-success-50 text-success-700',
        warning: 'bg-warning-50 text-warning-700',
        danger:  'bg-danger-50 text-danger-700',
    }[accent] || 'bg-neutral-100 text-neutral-600';

    return (
        <div className="card card-hover p-3 sm:p-4 animate-fade-in">
            <div className="flex items-start justify-between gap-2">
                <div className="text-[11px] font-medium uppercase leading-tight tracking-wide text-neutral-500">
                    {label}
                </div>
                {Icon && (
                    <div className={`hidden sm:flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${accentBg}`}>
                        <Icon className="h-3.5 w-3.5" />
                    </div>
                )}
            </div>

            <div className="mt-1.5">
                {loading ? (
                    <div className="h-6 w-20 animate-pulse-soft rounded bg-neutral-100" />
                ) : (
                    <div className="kpi-value text-lg font-semibold tabular-nums leading-tight text-neutral-900 sm:text-xl lg:text-2xl">
                        {value}
                    </div>
                )}
            </div>

            {(trend || hint) && (
                <div className="mt-1 flex items-center justify-between gap-2">
                    {hint && <div className="text-[11px] leading-tight text-neutral-500">{hint}</div>}
                    {trend && (
                        <div className={`inline-flex shrink-0 items-center gap-0.5 text-[11px] font-medium
                            ${trend.direction === 'up' ? 'text-success-700' : 'text-danger-700'}`}>
                            {trend.direction === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                            {trend.value}{trend.suffix || '%'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default StatTile;
